
import React from 'react';
import { useSelector } from 'react-redux';
import { Form, Input, Button, Col, Row, Space, Collapse, Card, Typography, message } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { MESSAGES } from '@translations/labels';
import { isEmpty } from '@utils/validation/isEmpty';

const VDN_EXAMPLE = `{
  "11032970005": {
    "PARTNER_REFERENCE": "BE-Reimbursement-status",
    "LOCALE": "fr",
    "ADDITIONAL_KEY": "example",
    "VDN_OUTPUT_PUBLIC": "11032970070",
    "VDN_OUTPUT_PRIVATE": "11032970006"
  },
  "11032970199": {
    "PARTNER_REFERENCE": "BE-Reimbursement-status",
    "LOCALE": "nl",
    "VDN_OUTPUT_PUBLIC": "11032970198",
    "VDN_OUTPUT_PRIVATE": "11032970069"
  }
}`;

const { Text } = Typography;

const VdnFormatGuide = () => (
  <Card
    title={
      <span>
        <InfoCircleOutlined style={{ marginRight: 8 }} />
        Expected Format
      </span>
    }
    size="small"
    style={{ backgroundColor: '#fafafa' }}
  >
    <Text strong>Validation rules:</Text>
    <ul style={{ paddingLeft: 20, marginTop: 8, marginBottom: 12 }}>
      <li>Must be a valid JSON object</li>
      <li>At least one VDN entry is required</li>
      <li>
        Each entry value must contain:
        <ul style={{ paddingLeft: 20, marginTop: 4 }}>
          <li>
            <Text code>PARTNER_REFERENCE</Text> (mandatory)
          </li>
          <li>
            <Text code>LOCALE</Text> (mandatory)
          </li>
          <li>
            At least one key starting with <Text code>VDN_OUTPUT</Text> (e.g.{' '}
            <Text code>VDN_OUTPUT_PUBLIC</Text>, <Text code>VDN_OUTPUT_PRIVATE</Text>)
          </li>
        </ul>
      </li>
      <li>Additional keys are allowed</li>
    </ul>
    <Text strong>Example:</Text>
    <pre
      style={{
        backgroundColor: '#f5f5f5',
        padding: 12,
        borderRadius: 4,
        fontSize: 12,
        marginTop: 8,
        overflowX: 'auto',
        maxHeight: 340,
        whiteSpace: 'pre',
      }}
    >
      {VDN_EXAMPLE}
    </pre>
  </Card>
);

const VdnSettingsForm = ({ onSave }) => {
  const { Panel } = Collapse;
  const [form] = Form.useForm();
  const { TextArea } = Input;
  const labels = { ...MESSAGES.manageBot, ...MESSAGES.commons };
  const vdnSettings = useSelector(state => state.vdnSettings.current);
  const loading = useSelector(state => state.vdnSettings.loading);

  const setValue = (field, text) => form.setFieldsValue({ [field]: text });

  if (!isEmpty(vdnSettings) && vdnSettings.vdn_map) {
    setValue('vdnData', JSON.stringify(vdnSettings.vdn_map, null, 2));
  }

  const validateContent = () => {
    const vdnData = form.getFieldValue('vdnData');
    try {
      const parsed = JSON.parse(vdnData);
      return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed);
    } catch {
      return false;
    }
  };

  const handleFinish = values => {
    if (validateContent()) {
      const parsed = JSON.parse(values.vdnData);
      if (onSave) {
        onSave(parsed);
      }
    } else {
      message.error('VDN map is not a valid JSON object');
    }
  };

  return (
    <Row gutter={16}>
      <Col span={12}>
        <Form layout="vertical" form={form} onFinish={handleFinish} hideRequiredMark scrollToFirstError>
          <Collapse defaultActiveKey={['1']}>
            <Panel header={labels.formVdnSettingsContent || 'VDN Settings'} key="1" open="false">
              <Form.Item
                name="vdnData"
                validateStatus={validateContent() ? 'success' : 'error'}
                rules={[
                  {
                    required: true,
                    message: `${labels.formVdnSettingsContent || 'VDN Settings'} ${labels.fieldRequired}`,
                  },
                ]}
              >
                <TextArea
                  placeholder={labels.formInputVdnSettingsContentPlaceholder || 'Enter VDN settings JSON...'}
                  maxLength={5000}
                  showCount
                  autoSize
                  allowClear
                />
              </Form.Item>
            </Panel>
          </Collapse>

          <Form.Item className="text-right">
            <Space size="middle">
              <Button type="primary" htmlType="submit" className="uppercase" loading={loading}>
                {labels.formSaveChanges}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Col>
      <Col span={12}>
        <VdnFormatGuide />
      </Col>
    </Row>
  );
};

export default VdnSettingsForm;
export { VdnSettingsForm };